import React from "react";
import Decimal from "decimal.js";
import {unregister as unregisterSW} from "../serviceWorker";
import parseBarcode from "../redux/rfid/BarcodeParser.min";

export const ltrim = function(string, char) {
    let index = 0;
    while (string[index] === char){
        index++;
    }
    return string.substr(index);
};

export const isEmptyString = (string) => {
    return ltrim(string, " ") === "";
}

export function interpolate(template, variables, fallback) {
    return template.replace(/\${[^{]+}/g, (match) => {
        const path = match.slice(2, -1).trim();
        return getObjPath(path, variables, fallback);
    });
}

function getObjPath(path, obj, fallback = '') {
    return path.split('.').reduce((res, key) => res[key] || fallback, obj);
}

export const getAttributeValue = (object, attributeName) => {
    if(typeof object.attributes === "undefined") {
        return undefined;
    }
    let attribute = object.attributes.find(el => el.attributeName === attributeName || el.name === attributeName);
    return typeof attribute !== "undefined" ? attribute?.attributeValue || attribute.value : undefined;
};

export const getProductPackageWeight = (product) => {
    let attributeValue = getAttributeValue(product, 'packageWeight');
    if(typeof attributeValue === "undefined") {
        return undefined;
    }
    return attributeValue.replace(',', '.');
};

export const capitalizeFirstLetter = (s) => {
    return s.substr(0,1).toUpperCase() + s.slice(1);
};

export const roundToDecimals = (num, toDecimals, displayDecimals=2) => {
    let step = 1 / toDecimals;
    return (Math.round(num * step)/step).toFixed(displayDecimals);
};

export const numberIsOdd = (number) => {
    return number % 2 === 1;
}

export const displayPrice = (price, decimals) => {
    const thousandsSeparator = window?.AppConf?.thousandsSeparator ?? "";
    const number = roundedPrice(price, decimals);
    if(thousandsSeparator !== ""){
        return numberWithThousandsSeparators(number, thousandsSeparator);
    }
    return number;
}

export const roundedPrice = (price, decimals=window?.AppConf?.priceDecimals ?? 2) => {
    return new Decimal(price).toFixed(decimals);
}

export const calculateNetPriceFromTotal = (price, vatrate) => {
    return (new Decimal(price)).div((new Decimal(vatrate)).div( 100).add(1)).toDecimalPlaces(6).toNumber();
}

export const calculateVatPrice = (netPrice, vatrate) => {
    return (new Decimal(netPrice)).mul((new Decimal(1)).plus((new Decimal(vatrate)).dividedBy(new Decimal(100)))).toDecimalPlaces(4).toNumber();
}

export const roundDown = (number, decimals) => {
    return new Decimal(number).toDecimalPlaces(decimals, Decimal.ROUND_DOWN).toNumber();
}

export const checkIfImageExists = (url) => {
    return new Promise(((resolve) => {
        const img = new Image();
        img.src = url;

        if (img.complete) {
            resolve(true);
        } else {
            img.onload = () => {
                resolve(true);
            };

            img.onerror = () => {
                resolve(false);
            };
        }
    }));
}

export const fetchWithTimeout = async (resource, options = {}) => {
    const { timeout = 30000 } = options;

    const controller = new AbortController();
    const id = setTimeout(() => controller.abort(), timeout);

    const response = await fetch(resource, {
        ...options,
        signal: controller.signal
    });
    clearTimeout(id);

    return response;
}

export const imageFile = (str) => {
    let regex = new RegExp(/[^\s]+(.*?).(jpg|jpeg|png|gif|ico|JPG|JPEG|PNG|GIF|ICO)$/);
    if (str == null) {
        return false;
    }
    return regex.test(str);
}

export const sleep = seconds => new Promise(resolve => setTimeout(resolve, seconds * 1000));

const urlWithRndQueryParam = (url, paramName) => {
    const ulrArr = url.split('#');
    const urlQry = ulrArr[0].split('?');
    const usp = new URLSearchParams(urlQry[1] || '');
    usp.set(paramName || '_z', `${Date.now()}`);
    urlQry[1] = usp.toString();
    ulrArr[0] = urlQry.join('?');
    return ulrArr.join('#');
}

export const handleHardReload = async(url, unregisterServiceWorker=false) => {
    const newUrl = urlWithRndQueryParam(url);
    await fetch(newUrl, {
        headers: {
            Pragma: 'no-cache',
            Expires: '-1',
            'Cache-Control': 'no-cache',
        },
    }).catch(() => {
        window.location.href = url;
    });
    if(unregisterServiceWorker){
        unregisterSW(() => {
            window.location.href = url;
        });
        //If service-worker does not become active
        setTimeout(() => {
            window.location.href = url;
        }, 5000);
    }else{
        window.location.href = url;
    }
}

export const hasVersionPreCache = (version="latest") => {
    return new Promise((resolve) => {
        if (caches) {
            caches.keys().then(function(names) {
                let precache = names.find(name => {
                    if(name.indexOf("workbox-precache") !== -1){
                        let versionNumber = name.split("/").find(el => {
                            return isSemanticVersionNumber(el);
                        }) ?? "latest";
                        return versionNumber === version;
                    }
                    return false;
                });
                resolve(typeof precache !== "undefined");
            }, () => {
                resolve(false);
            });
        }else{
            resolve(false);
        }
    });
}

export const unregisterVersionSW = (version="latest") => {
    navigator.serviceWorker.getRegistrations().then(registrationsArray => {
        for (let registration of registrationsArray){
            let versionNumber = registration.scope.split("/").find(el => {
                return isSemanticVersionNumber(el);
            }) ?? "latest";
            if(versionNumber === version){
                registration.unregister();
            }
        }
    });
}

export const checkAppPreCache = () => {
    const currentVersion = getVersionParamFromURL();

    hasVersionPreCache(currentVersion).then((hasCache) => {
        if(!hasCache){
            unregisterVersionSW(currentVersion);
        }
    });

    hasVersionPreCache().then((hasCache) => {
        if(!hasCache){
            unregisterVersionSW();
        }
    });
}
export const clearAppPreCache = (keepVersion) => {
    if (caches) {
        caches.keys().then(function(names) {
            for (let name of names){
                if(name.indexOf("workbox-precache") !== -1){
                    let versionNumber = name.split("/").find(el => {
                        return isSemanticVersionNumber(el);
                    });

                    if(typeof versionNumber !== "undefined"){
                        if(versionNumber !== keepVersion){
                            caches.delete(name);
                        }
                    }else{
                        caches.open(name).then((cache) => {
                            cache.matchAll("/manifest.json").then((responses) => {
                                if(responses.length === 0){
                                    navigator.serviceWorker.getRegistrations().then(registrationsArray => {
                                        for (let registration of registrationsArray){
                                            if(typeof registration.scope.split("/").find(el => {
                                                if(isSemanticVersionNumber(el)){
                                                    return  el !== keepVersion;
                                                }
                                                return false;
                                            }) === "undefined"){
                                                caches.delete(name).then(() => {
                                                    registration.unregister();
                                                });
                                            }
                                        }
                                    });
                                }
                            });
                        });
                    }
                }
            }
        });
    }
}

export function sFormat(template, data) {
    let templateString = Array.isArray(template) ? template.join("") : template;

    return templateString.split(/(\$\{\w+\})/g).map((part, i) => {
        const match = part.match(/^\$\{(\w+)\}$/);
        if (!match) return part;

        const key = match[1];
        let value = data[key];

        if (typeof value === "function") {
            value = value(key);
        }
        if (React.isValidElement(value)) {
            return React.cloneElement(value, { key: i });
        }

        return value ?? part;
    });
}

export function base64Encode(string){
    const bytes = new TextEncoder().encode(string);
    const binString = String.fromCodePoint(...bytes);
    return btoa(binString);
}

export function getObjectValues(obj){
    return Object.keys(obj).reduce((acc,el) => {
        if(typeof obj[el] !== "function"){
            acc[el] = obj[el];
        }
        return acc;
    }, {});
}

export const isSemanticVersionNumber = (text) => {
    return text.match(/^([0-9]+)\.([0-9]+)\.([0-9]+)?(\.([0-9]))?$/g) !== null;
}

export const isCashierModeForced = () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("cashierMode") === "true";
}
export const getAutologinMode = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const mode = urlParams.get("autologinMode");
    if(mode === "full" || mode === "lite"){
        return mode;
    }
    return false;
}

export const getPaymentSumInDefaultCurrency = (sum, rate) => {
    if(sum === "") return 0;
    return new Decimal(sum).mul(new Decimal(rate)).toDecimalPlaces(window?.AppConf?.priceDecimals ?? 2).toNumber();
}

export const numberWithThousandsSeparators = (number, separator) => {
    const decimalSeparator = window?.AppConf?.decimalSeparator ?? ".";
    let parts = number.toString().split(".");
    let decimalPart = parts?.[1] ?? "";
    if(decimalPart !== ""){
        decimalPart = decimalSeparator + decimalPart;
    }
    return parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, separator) + decimalPart;
}

export const getVersionParamFromURL = () => {
    let version = window.location.pathname.split('/')?.[1];
    if(version === ''){
        version = 'latest';
    }
    return version;
}

export const getSerialNumberFromBarcode = (code, trim=true) => {
    try{
        let barcode = parseBarcode(code);
        let serialItem = barcode.parsedCodeItems.find(el => el.dataTitle === 'SERIAL');
        return trim ? ltrim(serialItem.data, '0') : serialItem.data;
    }catch (e) {
        return false;
    }
}

export const createBarcode = (gtin, serial) => {
    return `01${gtin.padStart(14, "0")}21${serial.padStart(12, "0")}`;
}

export const mergeUniqueArrays = (a, b, key) => {
    const map = new Map();
    [...a, ...b].forEach(item => map.set(item[key], item));
    return [...map.values()];
};

export const cssStringToObject = (css) => {
    return css
        .split(";")
        .filter(Boolean)
        .reduce((acc, rule) => {
            const [key, value] = rule.split(":");
            const camelKey = key.trim().replace(/-([a-z])/g, (_, c) => c.toUpperCase());
            acc[camelKey] = value.trim();
            return acc;
        }, {});
}
